/**
 * ValidationRuleDialog - create/edit a single registration ValidationRule.
 *
 * Wraps the shared <ConditionBuilder> with the rule-specific metadata
 * (name, trigger step, severity, message, is_active) and persists via
 * the useValidationRules hooks.
 *
 * Form state is owned by the canonical `useForm` helper from
 * @/shared/lib/forms (see CLAUDE.md - Editable Detail Cards / form
 * generation rules). All field config is declarative; predicate edits
 * use the `custom` field type so the existing <ConditionBuilder> can be
 * embedded without bypassing form validation.
 */

import { useMemo } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateValidationRule,
  useUpdateValidationRule,
  type Severity,
  type ValidationRule,
  type ValidationRuleScope,
} from '@/admin/hooks/use-validation-rules'
import type { EntityTypeDefinition } from '@/admin/hooks/use-entities'
import { useRegistrationSettings, SEGMENT_LABELS } from '@/admin/hooks/use-registration-settings'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

import { toastApiError } from '@/shared/lib/api-error'
const SEVERITIES: { value: Severity; label: string }[] = [
  { value: 'BLOCK', label: 'Block - prevent submission' },
  { value: 'WARN', label: 'Warn - show warning but allow' },
  { value: 'INFO', label: 'Info - informational only' },
]

interface ValidationRuleDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  rule: ValidationRule | null
  entity?: EntityTypeDefinition
  defaultScope?: ValidationRuleScope
}

type RuleFormValues = {
  name: string
  trigger_step: string
  conditions: PredicateNode | null
  severity: Severity
  message: string
  is_active: boolean
}

export function ValidationRuleDialog({
  open,
  onOpenChange,
  rule,
  entity,
  defaultScope = 'registration_step',
}: ValidationRuleDialogProps) {
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs({
    enabled: open,
    entityKey: entity?.key,
  })
  const { data: settings } = useRegistrationSettings()
  const create = useCreateValidationRule()
  const update = useUpdateValidationRule()

  const stepOptions = useMemo(() => {
    const entitySegments = entity?.metadata?.registration?.segments ?? []
    const segments =
      entitySegments.length > 0
        ? entitySegments.map((segment) => ({
            value: segment.key,
            label: segment.label,
          }))
        : (settings?.segment_order ?? []).map((segment) => ({
            value: segment,
            label: SEGMENT_LABELS[segment] ?? segment,
          }))
    return [
      { value: '__any__', label: 'Any step' },
      ...segments,
    ]
  }, [entity?.metadata?.registration?.segments, settings?.segment_order])

  const initialValues: RuleFormValues = useMemo(
    () => ({
      name: rule?.name ?? '',
      trigger_step: rule?.trigger_step ?? '__any__',
      conditions: rule?.conditions ?? null,
      severity: rule?.severity ?? 'BLOCK',
      message: rule?.message ?? '',
      is_active: rule?.is_active ?? true,
    }),
    [rule],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        testId: 'rule-name',
        placeholder: 'Require phone for high-value purchases',
        className: 'col-span-1',
        value: initialValues.name,
        validationSchema: z.string().trim().min(1, 'Rule name is required'),
      },
      {
        type: 'select',
        name: 'trigger_step',
        label: 'Trigger step',
        className: 'col-span-1',
        value: initialValues.trigger_step,
        options: stepOptions,
      },
      {
        type: 'custom',
        name: 'conditions',
        label: 'Conditions',
        className: 'col-span-2',
        value: initialValues.conditions,
        validationSchema: z.any()
          .nullable()
          .refine(
            (value) => value !== null && value !== undefined,
            { message: 'Add at least one condition with a field, operator, and value' },
          ),
        render: (formContext) => {
          const value = (formContext.values.conditions ?? null) as PredicateNode | null
          const error = formContext.errors.conditions as string | undefined
          return (
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Conditions
              </Label>
              {refsLoading ? (
                <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                  Loading fields…
                </div>
              ) : (
                <ConditionBuilder
                  value={value}
                  onChange={(next) => formContext.setFieldValue('conditions', next)}
                  availableRefs={refs}
                  byRef={byRef}
                />
              )}
              {error && (
                <p className="text-[11px] text-[var(--status-error)]">{error}</p>
              )}
            </div>
          )
        },
      },
      {
        type: 'select',
        name: 'severity',
        label: 'Severity',
        testId: 'rule-severity',
        className: 'col-span-1',
        value: initialValues.severity,
        options: SEVERITIES,
      },
      {
        type: 'text',
        name: 'message',
        label: 'Message',
        testId: 'rule-message',
        placeholder: 'Shown to the admin when this rule matches',
        className: 'col-span-1',
        value: initialValues.message,
        validationSchema: z.string().trim().min(1, 'Rule message is required'),
      },
      {
        type: 'custom',
        name: 'is_active',
        className: 'col-span-2',
        value: initialValues.is_active,
        render: (formContext) => {
          const active = Boolean(formContext.values.is_active)
          return (
            <Button
              variant="ghost"
              type="button"
              onClick={() => formContext.setFieldValue('is_active', !active)}
              className="col-span-2 h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
            >
              <Switch checked={active} decorative />
              <div>
                <span className="block text-[12px] text-[var(--ink)]">
                  {active ? 'Active' : 'Inactive'}
                </span>
                <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
                  Inactive rules are retained but never evaluated.
                </span>
              </div>
            </Button>
          )
        },
      },
    ],
    [
      byRef,
      initialValues.conditions,
      initialValues.is_active,
      initialValues.message,
      initialValues.name,
      initialValues.severity,
      initialValues.trigger_step,
      refs,
      refsLoading,
      stepOptions,
    ],
  )

  const { Form, FormFooter } = useForm(
    {
      name: 'validation-rule-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as RuleFormValues
      const payload = {
        name: values.name.trim(),
        description: '',
        conditions: values.conditions,
        severity: values.severity,
        message: values.message.trim(),
        trigger_step: values.trigger_step === '__any__' ? null : values.trigger_step,
        is_active: values.is_active,
        scope: rule?.scope ?? defaultScope,
        entity_type:
          (rule?.scope ?? defaultScope) === 'entity_registration'
            ? rule?.entity_type ?? entity?.uuid ?? null
            : null,
      }
      try {
        if (rule) {
          await update.mutateAsync({ uuid: rule.uuid, ...payload })
          toast.success('Rule updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Rule created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to save rule')
      }
    },
    [defaultScope, entity?.uuid, fields, open, rule],
  )

  const busy = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit validation rule' : 'New validation rule'}</DialogTitle>
          <DialogDescription>
            Define when to block, warn, or inform during the registration flow.
          </DialogDescription>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={busy ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
